// @flow
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toastr } from 'react-redux-toastr';
import useIntl from '../../../hooks/useIntl';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import useHasPermission from '../../../hooks/useHasPermission';
import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import useApplicationFeesQuery from '../../../hooks/data-fetching/useApplicationFeesQuery';
// eslint-disable-next-line max-len
import useIncomeLevelByProgramQuery from '../../../hooks/data-fetching/useIncomeLevelByProgramQuery';
import useStatesQuery from '../../../hooks/data-fetching/useStatesQuery';
// eslint-disable-next-line max-len
import useAffordableDisplacementStatusCodesQuery from '../../../hooks/data-fetching/useAffordableDisplacementStatusCodesQuery';
// eslint-disable-next-line max-len
import useNameSuffixesQuery from '../../../hooks/data-fetching/useNameSuffixesQuery';
import {
  useFetchWaitlistConfiguration,
  useFetchWaitlistEditDetails,
} from '../../../hooks/data-fetching/useFetchWaitlistV2';
// eslint-disable-next-line max-len
import useAffordableWaitlistValidations from '../../ManageAffordableWaitlist/hooks.useAffordableWaitlistValidations';
// eslint-disable-next-line max-len
import useConvertProspectToApplicantMutation from '../../../hooks/data-modifying/useConvertProspectToApplicantMutation';
import { mapFloorPlanOptions } from '../../../utils/floorPlan';
import {
  getApplicationWindow,
  toAtomicProgramNames,
} from '../../../utils/affordable';
// eslint-disable-next-line max-len
import { navigateToUrlWithSelectedPropertyId } from '../../../utils/navigation-helpers';
import { buildIncomeFields } from '../sections/income';
import { buildPrimaryApplicantFields } from '../sections/primaryApplicant';
// eslint-disable-next-line max-len
import { buildUnitsAndPreferencesFields } from '../sections/unitsAndPreferences';
import { filterFloorPlanOptions, getBedroomOptions } from '../utils/bedrooms';
import { getFeeRows } from '../utils/fees';
import { toFiniteNumber } from '../../../utils/numbers';
import useDebouncedValue from '../../../hooks/useDebouncedValue';
import { toHouseholdSize } from '../utils/household';
import { getPreferenceOptions } from '../utils/preferences';
import { buildDefaultValues } from '../utils/prefill';
import { buildEditDefaultValues } from '../utils/editPrefill';
import {
  enforcesApplicationDateStart,
  formatApplicationDate,
  getApplicationDateRangeText,
  getProgramRows,
  getWaitlistClosedDate,
  hasConventionalUnits,
  isApplicationDateRestricted,
  markProgramAvailability,
  mergeProgramSummaries,
  toApplicationDate,
} from '../utils/programs';
import {
  getFieldVisibility,
  getFirstSectionLabelKey,
  isConventionalOnly,
  resolveFieldSet,
} from '../utils/sections';
import { toConvertPayload, toEditPayload } from '../utils/payload';
import {
  getMissingFieldLabels,
  getStandaloneErrorMessages,
} from '../utils/validation';
import { MODAL_MODES, SECTION_NAMES } from '../constants';
import messages from '../messages';

// What the Waitlist Closed banner says the agent may continue doing.
const WAITLIST_CLOSED_BANNER_BY_MODE = {
  [MODAL_MODES.CONVERT]: messages.waitlistClosedBanner,
  [MODAL_MODES.ADD]: messages.waitlistClosedBannerAdd,
  [MODAL_MODES.REINSTATE]: messages.waitlistClosedBannerReinstate,
  [MODAL_MODES.EDIT]: messages.waitlistClosedBannerEdit,
};

// Named on the backend for the field it guards rather than for the wizard, so
// it reads the same wherever the stamp turns up.
const EDIT_SUBMISSION_DATE_TIME_SCOPE = 'edit-waitlist-submission-date-time';

// One shared identity, so clearing the refusal on a valid submit does not hand
// the modal a new object to re-render on.
const NO_SUBMIT_ERRORS = {
  missingFieldLabels: [],
  standaloneErrorMessages: [],
};

const useConvertToApplicantModal = ({
  currentProspect,
  floorPlans,
  onClose,
  mode = MODAL_MODES.CONVERT,
  applicationId,
  onSubmit,
}: {
  currentProspect: ?Object,
  floorPlans: ?Array<Object>,
  onClose: Function,
  mode?: string,
  applicationId?: ?string,
  onSubmit?: ?Function,
}): Object => {
  const isConvert = mode === MODAL_MODES.CONVERT;
  // Add creates the waitlist record rather than reopening one, so on the two
  // questions about the submission stamp — who may set it, and what it opens
  // on — it belongs with convert, not with edit and reinstate.
  const isAdd = mode === MODAL_MODES.ADD;
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const propertyId = selectedProperty?.id;
  const organizationId = selectedProperty?.organizationId;
  const propertyClassName = selectedProperty?.propertyClass?.name;
  const propertyTimezone = selectedProperty?.config?.timezone;

  const { propertyAffordablePrograms } = useAffordableWaitlistValidations();

  // A layered property stores its funding as one master program named
  // `LIHTC/HOME`; splitting here rather than in each consumer is what keeps the
  // Selected Program(s) table and the field-set rule answering the same
  // question about what programs the property has.
  const affordableProgramNames = useMemo(
    () => toAtomicProgramNames(propertyAffordablePrograms),
    [propertyAffordablePrograms],
  );

  // The waitlist configuration, not the property setup, is what says whether a
  // waitlist runs and which programs it covers, so the Selected Program(s)
  // table reads its checkmarks from here.
  const { data: waitlistConfiguration } = useFetchWaitlistConfiguration(
    propertyId ?? '',
  );

  const applicationWindow = useMemo(
    () => getApplicationWindow(waitlistConfiguration),
    [waitlistConfiguration],
  );

  // The Application Date programs are judged by. Held apart from the field so
  // it moves only when the field is left on a valid date; a half-typed or
  // cleared date leaves the last valid one in charge. Convert seeds the field
  // with now, which is also what stands in while the field is hidden.
  const [applicationDate, setApplicationDate] = useState(() => new Date());

  // Edit and reinstate open on the saved waitlist record. The request is
  // skipped outright in convert mode, where there is no record yet.
  const { data: waitlistDetails, isLoading: isPrefillLoading } =
    useFetchWaitlistEditDetails(
      organizationId ?? '',
      propertyId ?? '',
      applicationId ?? '',
      !isConvert,
    );

  // The date the record was saved with, against which reinstate tells a kept
  // date from a moved one and add tells a legacy row from a first entry.
  const storedApplicationDate = useMemo(
    () =>
      isConvert ? null : toApplicationDate(waitlistDetails?.submissionDateTime),
    [isConvert, waitlistDetails],
  );

  // react-hook-form fixes defaultValues at mount and the request above lands
  // later, so this is only the opening shape; the reset below is what actually
  // seeds an edit.
  const defaultValues = useMemo(
    () =>
      isConvert
        ? buildDefaultValues({ currentProspect, floorPlans })
        : buildEditDefaultValues(null),
    [isConvert, currentProspect, floorPlans],
  );

  // One react-hook-form instance shared by all four section Forms, so a submit
  // validates every section at once and one section can watch another's values.
  const reactHookFormProps = useReactHookFormProps({
    mode: 'onChange',
    defaultValues,
    shouldUnregister: true,
  });
  const { watch, getValues, handleSubmit, reset, setValue, trigger } =
    reactHookFormProps;

  // The record arrives after mount, so the seeded values are pushed in with a
  // reset rather than through defaultValues. Keyed on the response object, so
  // a refetch that returns the same data does not stamp over an in-progress
  // edit.
  // Add opens on an application that has never been on the waitlist, so the
  // response carries no stamp — and the field is `required` wherever it is
  // editable, which on add it is. The default is applied here, inside the one
  // reset, rather than from a second effect: a `setValue` racing this reset is
  // how the whole prefill got lost once already.
  useEffect(() => {
    if (!isConvert && waitlistDetails) {
      const values = buildEditDefaultValues(waitlistDetails);
      const seeded =
        isAdd && !values.submissionDateTime
          ? { ...values, submissionDateTime: new Date() }
          : values;

      reset(seeded);

      const seededApplicationDate = toApplicationDate(
        seeded.submissionDateTime,
      );

      if (seededApplicationDate) {
        setApplicationDate(seededApplicationDate);
      }
    }
  }, [isConvert, isAdd, waitlistDetails, reset]);

  const adultApplicantsCount = toFiniteNumber(watch('adults'));
  const householdAnnualIncome = watch('householdAnnualIncome');
  const minors = watch('minors');
  const selectedBedrooms = watch('bedrooms');
  const selectedPrograms = watch('selectedPrograms');
  const selectedFloorPlan = watch('floorPlan');
  const requestAccommodation = watch('requestAccommodation');

  // The accommodation detail is required only while an accommodation is being
  // requested, and nothing about the detail field itself changes when that
  // answer is withdrawn — so its own validation has to be asked for, or the
  // refusal stays on screen after the answer that caused it is gone. Only on
  // the way out of Yes: the other direction would fault a field the agent has
  // not reached yet.
  useEffect(() => {
    if (requestAccommodation !== 'yes') {
      trigger('accommodationRequested');
    }
  }, [requestAccommodation, trigger]);

  const { data: applicationFees } = useApplicationFeesQuery({
    organizationId,
    propertyId,
    adultApplicantsCount,
    floorPlanId: selectedFloorPlan,
    options: { enabled: isConvert },
  });

  // The income is free text with no mask, so typing 42000 would key the query
  // five times. The household size is debounced with it rather than on its own:
  // sending a new count against the previous income scores a household that was
  // never entered, and renders that answer as final until the next request lands.
  // Memoized so the pair keeps one identity while its parts are unchanged;
  // useDebouncedValue restarts its timer on a new reference, and a fresh object
  // literal every render would never settle.
  const incomeLevelInputs = useMemo(
    () => ({
      anticipatedAnnualIncome: householdAnnualIncome,
      householdSize: toHouseholdSize(adultApplicantsCount, minors),
    }),
    [householdAnnualIncome, adultApplicantsCount, minors],
  );

  const debouncedIncomeLevelInputs = useDebouncedValue(incomeLevelInputs);

  const { data: incomeLevelByProgram, isError: isIncomeLevelError } =
    useIncomeLevelByProgramQuery({
      organizationId,
      propertyId,
      ...debouncedIncomeLevelInputs,
    });

  const { data: states, isLoading: isStatesLoading } = useStatesQuery();
  const { data: displacementStatusCodes } =
    useAffordableDisplacementStatusCodesQuery({ organizationId });
  const { nameSuffixOptions, isLoading: isSuffixesLoading } =
    useNameSuffixesQuery();

  const bedroomOptions = useMemo(
    () => getBedroomOptions(floorPlans),
    [floorPlans],
  );

  // The shape every other state select in the app is fed.
  const stateOptions = useMemo(
    () =>
      (Array.isArray(states) ? states : []).map(({ id, name }) => ({
        value: id,
        text: name,
      })),
    [states],
  );

  // Ordered by the transmitted code so the list reads 1 through 4 rather than
  // however the API returned it.
  const displacementStatusCodeOptions = useMemo(
    () =>
      (Array.isArray(displacementStatusCodes) ? displacementStatusCodes : [])
        .slice()
        .sort((a, b) => Number(a.code) - Number(b.code))
        .map(({ id, name }) => ({ value: id, text: name })),
    [displacementStatusCodes],
  );

  const floorPlanOptions = useMemo(
    () =>
      filterFloorPlanOptions(
        mapFloorPlanOptions(floorPlans ?? []),
        selectedBedrooms,
      ),
    [floorPlans, selectedBedrooms],
  );

  // A bedroom edit narrows the list out from under an already-chosen plan, and
  // seeding the field from the prospect is what makes that common. Mirrors the
  // same guard on the prospect side, in LookingFor.hook.js.
  useEffect(() => {
    const isStillOffered = floorPlanOptions.some(
      ({ value }) => value === selectedFloorPlan,
    );

    if (selectedFloorPlan && !isStillOffered) {
      setValue('floorPlan', '');
    }
  }, [floorPlanOptions, selectedFloorPlan, setValue]);

  const preferenceOptions = useMemo(
    () => getPreferenceOptions(waitlistConfiguration?.preferences),
    [waitlistConfiguration],
  );

  // Which programs the property has, which changes with its setup — kept apart
  // from how this household scores against them, which changes as the agent
  // edits the income and the counts.
  const propertyProgramRows = useMemo(
    () =>
      getProgramRows({
        propertyAffordablePrograms: affordableProgramNames,
        waitlistConfiguration,
        propertyClassName,
      }),
    [affordableProgramNames, waitlistConfiguration, propertyClassName],
  );

  const programRows = useMemo(
    () =>
      markProgramAvailability(
        mergeProgramSummaries(
          propertyProgramRows,
          incomeLevelByProgram?.programs,
        ),
        {
          range: applicationWindow.range,
          applicationDate,
          isRestricted: isApplicationDateRestricted({
            mode,
            hasPolicy: applicationWindow.hasPolicy,
            storedApplicationDate,
            applicationDate,
          }),
          enforceStart: enforcesApplicationDateStart({
            mode,
            storedApplicationDate,
            applicationDate,
          }),
        },
      ),
    [
      propertyProgramRows,
      incomeLevelByProgram,
      applicationWindow,
      applicationDate,
      mode,
      storedApplicationDate,
    ],
  );

  const unavailableProgramTooltip = useMemo(
    () =>
      intl.formatMessage(messages.programUnavailableTooltip, {
        range: getApplicationDateRangeText(
          intl,
          applicationWindow,
          propertyTimezone,
        ),
      }),
    [intl, applicationWindow, propertyTimezone],
  );

  // Said while a closed waitlist still takes the household in, so the agent
  // knows the Application Date is what programs answer to. A closed waitlist
  // that accepts no date (no range) takes no one.
  const waitlistClosedBannerMessage = useMemo(() => {
    if (!applicationWindow.isClosed || !applicationWindow.acceptsAnyDate) {
      return null;
    }

    const closedDate = getWaitlistClosedDate(waitlistConfiguration);

    return closedDate
      ? intl.formatMessage(
          WAITLIST_CLOSED_BANNER_BY_MODE[mode] ?? messages.waitlistClosedBanner,
          {
            lead: intl.formatMessage(messages.waitlistClosedLead),
            closeDate: formatApplicationDate(closedDate, propertyTimezone),
          },
        )
      : null;
  }, [intl, mode, applicationWindow, waitlistConfiguration, propertyTimezone]);

  // A program the new date rules out is unticked as the date changes, rather
  // than left ticked for the backend to refuse on save.
  const recheckProgramAvailability = useCallback(
    (value: mixed) => {
      const nextApplicationDate = toApplicationDate(value);

      if (!nextApplicationDate) {
        return;
      }

      setApplicationDate(nextApplicationDate);

      const unavailable = new Set(
        markProgramAvailability(propertyProgramRows, {
          range: applicationWindow.range,
          applicationDate: nextApplicationDate,
          isRestricted: isApplicationDateRestricted({
            mode,
            hasPolicy: applicationWindow.hasPolicy,
            storedApplicationDate,
            applicationDate: nextApplicationDate,
          }),
          enforceStart: enforcesApplicationDateStart({
            mode,
            storedApplicationDate,
            applicationDate: nextApplicationDate,
          }),
        })
          .filter(({ available }) => !available)
          .map(({ name }) => name),
      );
      const ticked = getValues('selectedPrograms') ?? [];
      const kept = ticked.filter((name) => !unavailable.has(name));

      if (kept.length !== ticked.length) {
        setValue('selectedPrograms', kept, { shouldValidate: true });
      }
    },
    [
      getValues,
      setValue,
      propertyProgramRows,
      applicationWindow,
      mode,
      storedApplicationDate,
    ],
  );

  const handleSubmissionDateTimeBlur = useCallback(
    () => recheckProgramAvailability(getValues('submissionDateTime')),
    [recheckProgramAvailability, getValues],
  );

  const feeRows = useMemo(() => getFeeRows(applicationFees), [applicationFees]);

  const propertyIsConventionalOnly = isConventionalOnly(affordableProgramNames);

  // Which fields the configuration asks for. The ticked programs are an input
  // because a mixed property moves between field sets while the agent works.
  const fieldSet = useMemo(
    () =>
      resolveFieldSet({
        affordableProgramNames,
        waitlistEnabled: Boolean(waitlistConfiguration?.enabled),
        selectedPrograms,
        hasConventionalUnits: hasConventionalUnits(propertyClassName),
        // The query holds undefined until it resolves; a property with no
        // configuration resolves to null.
        isWaitlistConfigurationLoaded: waitlistConfiguration !== undefined,
      }),
    [
      affordableProgramNames,
      waitlistConfiguration,
      selectedPrograms,
      propertyClassName,
    ],
  );

  const fieldVisibility = useMemo(
    () =>
      getFieldVisibility(fieldSet, {
        usePreferencesForRanking: Boolean(
          waitlistConfiguration?.usePreferencesForRanking,
        ),
      }),
    [fieldSet, waitlistConfiguration],
  );

  const {
    showHouseholdIncome,
    showSubmissionStamp,
    showDemographics,
    showMailingAddress,
    showAccommodation,
    showDisplacement,
    showContactInformation,
    showPreferences,
    noPreferenceOptionEnabled,
  } = fieldVisibility;

  const hasEditSubmissionDateTimePermission = useHasPermission(
    EDIT_SUBMISSION_DATE_TIME_SCOPE,
  );

  // Convert and add both stamp the record as they create it, so there is no
  // earlier value for the permission to protect; edit and reinstate both reopen
  // a stamp that has already been set, and that is what it guards.
  const canEditSubmissionDateTime =
    isConvert || isAdd || hasEditSubmissionDateTimePermission;

  // The stamp is seeded at mount, but the field it belongs to is hidden until
  // the waitlist configuration resolves, and `shouldUnregister` drops a hidden
  // field's value — so it arrives blank. Re-seed it as it appears. Guarded on
  // being empty, so an agent who clears the stamp keeps it cleared.
  useEffect(() => {
    if (isConvert && showSubmissionStamp && !getValues('submissionDateTime')) {
      setValue('submissionDateTime', new Date());
    }
  }, [isConvert, showSubmissionStamp, getValues, setValue]);

  // The definitions are both what the sections render and where the labels for
  // a refused convert come from, so they are built once here rather than in the
  // component that draws them.
  const sectionFields = useMemo(
    () => ({
      [SECTION_NAMES.INCOME]: buildIncomeFields({ intl, showHouseholdIncome }),
      [SECTION_NAMES.PRIMARY_APPLICANT]: buildPrimaryApplicantFields({
        intl,
        showSubmissionStamp,
        canEditSubmissionDateTime,
        onSubmissionDateTimeBlur: handleSubmissionDateTimeBlur,
        onSubmissionDateTimeAccept: recheckProgramAvailability,
        showDemographics,
        showMailingAddress,
        showContactInformation,
        stateOptions,
        isStatesLoading,
        suffixOptions: nameSuffixOptions,
        isSuffixesLoading,
      }),
      [SECTION_NAMES.UNITS_AND_PREFERENCES]: buildUnitsAndPreferencesFields({
        intl,
        bedroomOptions,
        floorPlanOptions,
        preferenceOptions,
        showAccommodation,
        showDisplacement,
        displacementStatusCodeOptions,
        showPreferences,
        noPreferenceOptionEnabled,
      }),
      [SECTION_NAMES.FEES_AND_DEPOSITS]: [],
    }),
    [
      intl,
      bedroomOptions,
      floorPlanOptions,
      preferenceOptions,
      stateOptions,
      isStatesLoading,
      nameSuffixOptions,
      isSuffixesLoading,
      showHouseholdIncome,
      showSubmissionStamp,
      canEditSubmissionDateTime,
      handleSubmissionDateTimeBlur,
      recheckProgramAvailability,
      showDemographics,
      showMailingAddress,
      showContactInformation,
      showAccommodation,
      showDisplacement,
      displacementStatusCodeOptions,
      showPreferences,
      noPreferenceOptionEnabled,
    ],
  );

  // Both halves of one refusal: the fields the section definitions can name,
  // and the messages carried by errored fields they do not — selectedPrograms,
  // which the app renders itself. Always written and cleared together, so one
  // piece of state rather than two that could drift apart.
  const [submitErrors, setSubmitErrors] = useState(NO_SUBMIT_ERRORS);

  const { mutate: convertProspect, isLoading: isConverting } =
    useConvertProspectToApplicantMutation({
      organizationId,
      propertyId,
      options: {
        // Legacy parity with the create-application path: the agent lands on
        // the record they just made rather than back on the prospect.
        onSuccess: ({ applicationId }) => {
          toastr.success('Success', 'Prospect converted to applicant');
          onClose();
          navigateToUrlWithSelectedPropertyId(`/application/${applicationId}`);
        },
        // The route answers a refusal as Boom.badRequest, so the message is
        // the backend's own and displayable.
        onError: (e) => toastr.error('Error', e?.message ?? e),
      },
    });

  const onValid = useCallback(() => {
    setSubmitErrors(NO_SUBMIT_ERRORS);

    if (!isConvert) {
      // Which keys the body carries is decided by what the configuration
      // renders, so the same visibility that drove the form drives the PATCH.
      onSubmit?.(
        toEditPayload(getValues(), fieldVisibility, {
          canEditSubmissionDateTime,
        }),
      );
      return;
    }

    convertProspect({
      prospectId: currentProspect?.id,
      payload: toConvertPayload(getValues()),
    });
  }, [
    isConvert,
    onSubmit,
    fieldVisibility,
    canEditSubmissionDateTime,
    convertProspect,
    currentProspect,
    getValues,
  ]);

  const onInvalid = useCallback(
    (errors) =>
      setSubmitErrors({
        missingFieldLabels: getMissingFieldLabels(sectionFields, errors),
        standaloneErrorMessages: getStandaloneErrorMessages(
          sectionFields,
          errors,
        ),
      }),
    [sectionFields],
  );

  const submit = useMemo(
    () => handleSubmit(onValid, onInvalid),
    [handleSubmit, onValid, onInvalid],
  );

  return {
    reactHookFormProps,
    sectionFields,
    submit,
    isConverting,
    missingFieldLabels: submitErrors.missingFieldLabels,
    standaloneErrorMessages: submitErrors.standaloneErrorMessages,
    isConventionalOnly: propertyIsConventionalOnly,
    firstSectionLabelKey: getFirstSectionLabelKey(fieldSet),
    programRows,
    unavailableProgramTooltip,
    waitlistClosedBannerMessage,
    feeRows,
    // A failed score and a program with no published limits both leave the row
    // unscored; without this the table would render them identically.
    isIncomeLevelError,
    isPrefillLoading: !isConvert && isPrefillLoading,
  };
};

export default useConvertToApplicantModal;
